import {
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react'
import { useState } from 'react'
import { LuBug } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'

import { useSubmitBugReport } from '../../../api/api_hooks/bugReports'

/**
 * Manual "Signaler un bug" — reachable from the "Astuces d'édition" popover.
 * Unlike FeedbackModal (an NPS survey shown once, automatically), this is a
 * free-text report the user can open and send as many times as they like.
 * Submitting POSTs to /api/bug-reports, which emails the product owner
 * directly (same "goes straight to a person" pattern as Feedback).
 */
export default function BugReportModal({
  isOpen,
  onClose,
  context,
}: {
  isOpen: boolean
  onClose: () => void
  /** Light metadata stored + emailed with the report (e.g. current case id). */
  context?: Record<string, unknown>
}) {
  const toast = useToast()
  const { t } = useTranslation()
  const { mutateAsync: submitBugReport, isPending } = useSubmitBugReport()
  const [description, setDescription] = useState('')

  const handleClose = () => {
    setDescription('')
    onClose()
  }

  const submit = async () => {
    try {
      await submitBugReport({
        description: description.trim(),
        context: { ...context, url: window.location.href, userAgent: navigator.userAgent },
      })
      toast({ title: t('blotAnalysis.bugReport.sent'), status: 'success', duration: 4000 })
      handleClose()
    } catch {
      toast({ title: t('blotAnalysis.common.sendFailed'), description: t('blotAnalysis.common.retryLater'), status: 'error', duration: 4000 })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader p={0}>
          <Box bgGradient="linear(135deg, red.500, orange.400)" color="white" px={6} py={5}>
            <HStack spacing={3}>
              <Box bg="whiteAlpha.300" borderRadius="xl" p={2.5}>
                <Icon as={LuBug} boxSize={6} />
              </Box>
              <Box>
                <Text fontSize="lg" fontWeight="bold" lineHeight="1.2">{t('blotAnalysis.bugReport.title')}</Text>
                <Text fontSize="sm" color="whiteAlpha.900">{t('blotAnalysis.bugReport.subtitle')}</Text>
              </Box>
            </HStack>
          </Box>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} right={4} />

        <ModalBody px={6} py={5}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.800" mb={2}>
            {t('blotAnalysis.bugReport.whatHappened')}
          </Text>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('blotAnalysis.bugReport.placeholder')}
            rows={6}
            fontSize="sm"
            resize="vertical"
            autoFocus
          />
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
          <Button variant="ghost" color="gray.500" onClick={handleClose} isDisabled={isPending}>
            Annuler
          </Button>
          <Button
            colorScheme="red"
            onClick={submit}
            isLoading={isPending}
            isDisabled={!description.trim()}
            borderRadius="lg"
          >
            Envoyer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
